import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { patientsApi, type CreatePatientInput, type UpdatePatientInput } from '@/api/patientsApi'

export interface PatientListParams {
  q?: string
  procedureTemplateId?: string
  skip?: number
  take?: number
  /** `column:asc|desc`, applied server-side before paging (z8nrz7e4h6). */
  sort?: string
}

/** Prefix key for invalidating every patient query (list + detail). */
const PATIENTS_KEY = ['patients'] as const

/** Paginated patients list, plus the create and sync mutations that invalidate it. */
export function usePatients(params: PatientListParams = {}) {
  const qc = useQueryClient()

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: PATIENTS_KEY })
  }

  const list = useQuery({
    queryKey: ['patients', 'list', params],
    queryFn: () => patientsApi.list(params),
    // Keeps the previous page on screen while the next one loads, so paging and
    // typing in the search box don't blank the table.
    placeholderData: keepPreviousData,
  })

  const create = useMutation({
    mutationFn: (input: CreatePatientInput) => patientsApi.create(input),
    onSuccess: invalidate,
  })

  const sync = useMutation({
    mutationFn: () => patientsApi.sync(),
    onSuccess: invalidate,
  })

  return { list, create, sync, invalidate }
}

/**
 * One patient by MRN, with demographics and per-procedure step progress.
 *
 * Shared by the detail page and `PatientBanner`, which renders on the procedure and
 * form-renderer pages too — they hit the same query key, so the banner costs no
 * extra request on a page that already loaded the patient.
 */
export function usePatient(mrn: string) {
  return useQuery({
    queryKey: ['patients', 'detail', mrn],
    queryFn: () => patientsApi.get(mrn),
    enabled: Boolean(mrn),
  })
}

/** Create-only hook for the dedicated create page, which has no list to invalidate locally. */
export function useCreatePatient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePatientInput) => patientsApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PATIENTS_KEY })
    },
  })
}

/**
 * Correct a patient's demographics (z8nrz7dp78). The response is written straight
 * into the detail cache so the card, age and banner change at once, then every
 * patient query is refetched: the list's Name column, the board's patient options,
 * and each case banner all read under the `patients` prefix (AC3.1).
 */
export function useUpdatePatient(mrn: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdatePatientInput) => patientsApi.update(mrn, input),
    onSuccess: updated => {
      qc.setQueryData(['patients', 'detail', mrn], updated)
      void qc.invalidateQueries({ queryKey: PATIENTS_KEY })
    },
  })
}
